/**
 * Utilities for WHO block event/transaction date-time filter.
 * Mode: fixed | last | since
 * Default: last 180 days
 */

export const WHO_TIME_UNITS = [
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

export const DEFAULT_WHO_TIME_FILTER = {
  mode: 'last',
  lastAmount: 180,
  lastUnit: 'days',
};

function combineDateTime(dateStr, timeStr = '00:00') {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const [h, m] = (timeStr || '00:00').split(':').map(Number);
  d.setHours(isNaN(h) ? 0 : h, isNaN(m) ? 0 : m, 0, 0);
  return d;
}

function getOffsetMs(amount, unit) {
  const multipliers = {
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
    weeks: 7 * 24 * 60 * 60 * 1000,
    months: 30 * 24 * 60 * 60 * 1000,
  };
  return (amount || 0) * (multipliers[unit] || multipliers.days);
}

/**
 * Compute { from, to } ISO strings from eventTimeFilter for backend
 */
export function computeWhoTimeRange(tf) {
  if (!tf || !tf.mode) return null;
  const now = new Date();

  if (tf.mode === 'fixed') {
    if (!tf.startDate || !tf.endDate) return null;
    const enableTime = !!tf.enableTimeRanges;
    const from = combineDateTime(tf.startDate, enableTime ? tf.startTime : '00:00');
    const to = combineDateTime(tf.endDate, enableTime ? tf.endTime : '23:59');
    if (!from || !to) return null;
    return { from: from.toISOString(), to: to.toISOString() };
  }

  if (tf.mode === 'last') {
    const amount = tf.lastAmount ?? 180;
    const unit = tf.lastUnit ?? 'days';
    const endingAmount = tf.endingAmount ?? 0;
    const endingUnit = tf.endingUnit ?? 'days';
    const endingOffset = getOffsetMs(endingAmount, endingUnit);
    const windowSize = getOffsetMs(amount, unit);
    const endDate = new Date(now.getTime() - endingOffset);
    const startDate = new Date(endDate.getTime() - windowSize);
    return { from: startDate.toISOString(), to: endDate.toISOString() };
  }

  if (tf.mode === 'since') {
    if (!tf.sinceDate) return null;
    const enableTime = !!tf.enableTimeRanges;
    const from = combineDateTime(tf.sinceDate, enableTime ? tf.sinceTime : '00:00');
    if (!from) return null;
    const to = enableTime ? now : combineDateTime(now.toISOString().split('T')[0], '23:59');
    return { from: from.toISOString(), to: to.toISOString() };
  }

  return null;
}

/**
 * Format short label for chip display
 */
export function formatWhoTimeFilterLabel(tf) {
  if (!tf || !tf.mode) return 'Last 180d';
  if (tf.mode === 'last') {
    const a = tf.lastAmount ?? 180;
    const u = (tf.lastUnit ?? 'days').slice(0, 1);
    return `Last ${a}${u}`;
  }
  if (tf.mode === 'fixed' && tf.startDate && tf.endDate) {
    const s = new Date(tf.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const e = new Date(tf.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${s} - ${e}`;
  }
  if (tf.mode === 'since' && tf.sinceDate) {
    const d = new Date(tf.sinceDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `Since ${d}`;
  }
  return 'Last 180d';
}
